import { CogIcon } from "@heroicons/react/24/outline";
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/core/components/ui/button";
import { Label } from "@/core/components/ui/label";
import { Stack } from "@/core/components/ui/stack";
import { Switch } from "@/core/components/ui/switch";
import { HeaderStack } from "@/business-settings/common/business-settings-page";
import { PageHeader } from "@/business-settings/common/page-header";
import { useBusiness } from "@/core/business";
import { LockPeriodModal } from "./lock-period-modal";
import { PayrollPeriodsContent } from "./payroll-periods-content";
import { useGetEnrichedPayrollPeriods } from "./query";

const PayrollPeriods = () => {
  const { businessId } = useBusiness();
  const navigate = useNavigate();
  const [lockingPeriodId, setLockingPeriodId] = useState<string | null>(null);
  const [flatViewByPeriod, setFlatViewByPeriod] = useState<
    Record<string, boolean>
  >({});
  const [expandedPeriods, setExpandedPeriods] = useState<
    Record<string, boolean>
  >({});
  const firstTimelineRef = useRef<HTMLDivElement>(null);
  const lastTimelineRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { periodsWithSpiffs = [], isLoading } =
    useGetEnrichedPayrollPeriods(businessId);

  const lockingPeriod = periodsWithSpiffs.find(
    (g) => g.period.id === lockingPeriodId,
  );

  useEffect(() => {
    if (
      periodsWithSpiffs.length > 0 &&
      Object.keys(expandedPeriods).length === 0
    ) {
      const initialExpandedState: Record<string, boolean> = {};
      periodsWithSpiffs.forEach((period) => {
        initialExpandedState[period.period.id] =
          period.period.status === "current";
      });
      setExpandedPeriods(initialExpandedState);
    }
  }, [periodsWithSpiffs, expandedPeriods]);

  const handleToggleExpanded = (periodId: string) => {
    setExpandedPeriods((prev) => ({
      ...prev,
      [periodId]: !prev[periodId],
    }));
  };

  const handleGlobalToggle = (checked: boolean) => {
    const newFlatViewState: Record<string, boolean> = {};
    periodsWithSpiffs.forEach((period) => {
      newFlatViewState[period.period.id] = !checked;
    });
    setFlatViewByPeriod(newFlatViewState);
  };

  const handlePeriodToggle = (periodId: string, isFlatView: boolean) => {
    setFlatViewByPeriod((prev) => ({
      ...prev,
      [periodId]: isFlatView,
    }));
  };

  const allGrouped =
    periodsWithSpiffs.length > 0 &&
    periodsWithSpiffs.every((p) => flatViewByPeriod[p.period.id] === false);

  return (
    <>
      <div className="mx-auto w-full max-w-[85rem] px-6 pt-8">
        <HeaderStack>
          <PageHeader>Spiffs</PageHeader>
          <Stack direction="row" align="center" gap="md">
            <Stack direction="row" align="center" gap="sm">
              <Label
                htmlFor="global-group-by-technician"
                className="whitespace-nowrap font-inter text-md font-normal text-muted-foreground"
              >
                Group by technician
              </Label>
              <Switch
                id="global-group-by-technician"
                checked={allGrouped}
                onCheckedChange={handleGlobalToggle}
                className="h-6 w-12 [&>span]:h-5 [&>span]:w-5 [&>span]:data-[state=checked]:translate-x-6 [&>span]:data-[state=unchecked]:translate-x-0"
              />
            </Stack>
            <Button
              size="sm"
              variant="secondary"
              leftIcon={<CogIcon className="h-4 w-4 min-w-4" />}
              onClick={() => navigate("/spiffs/spiff-programs")}
            >
              Spiff Rules
            </Button>
          </Stack>
        </HeaderStack>

        <PayrollPeriodsContent
          periodsWithSpiffs={periodsWithSpiffs}
          isLoading={isLoading}
          containerRef={containerRef}
          firstTimelineRef={firstTimelineRef}
          lastTimelineRef={lastTimelineRef}
          onLockPeriod={setLockingPeriodId}
          flatViewByPeriod={flatViewByPeriod}
          onPeriodToggle={handlePeriodToggle}
          expandedPeriods={expandedPeriods}
          onToggleExpanded={handleToggleExpanded}
        />
      </div>

      <LockPeriodModal
        periodId={lockingPeriodId}
        groupedPeriod={lockingPeriod}
        onClose={() => setLockingPeriodId(null)}
      />
    </>
  );
};

export default PayrollPeriods;
